import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Ban, Download, Loader2, Mail, ReceiptText } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, promptDialog, toast } from '../../lib/dialogs'
import { downloadFile } from '../../lib/download'
import { currentLocaleTag } from '../../lib/formatting'
import { formatCents } from '../../lib/money'
import { useTableSort } from '../../lib/useTableSort'
import Modal from '../ui/Modal'
import type { AdvisorDto, AdvisorSettlementDto, AdvisorSummary } from '@jr2os/shared'

function errorMessage(err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
  return body?.error ?? (err instanceof Error ? err.message : 'Error')
}

/**
 * Detalle de un asesor (Ajustes → Asesores): lo que ha generado en un año, liquidarlo y mandarle el informe.
 *
 * ☠️ «Liquidar» es un documento y un registro, NO un pago: no crea gasto, ni pago, ni asiento (el aviso va en pantalla).
 * Los importes los calcula el servidor (`GET /advisors/:id/summary`); aquí solo se pintan y se disparan las acciones.
 */
export default function AdvisorDetailModal({
  advisor,
  onClose,
}: {
  advisor: AdvisorDto
  onClose: () => void
}) {
  const { t } = useTranslation(['settings', 'enums'])
  const tag = currentLocaleTag()
  const [year, setYear] = useState(new Date().getFullYear())
  const [data, setData] = useState<AdvisorSummary | null>(null)
  const [years, setYears] = useState<number[]>([year])
  const [error, setError] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  // Cada petición lleva su número: si el usuario cambia de año rápido, una respuesta lenta del año anterior no puede pisar a
  // la del año nuevo (y las acciones de abajo usan el año de los DATOS que se ven, no el del selector).
  const requestSeq = useRef(0)

  const load = useCallback(
    async (clear: boolean) => {
      const mine = ++requestSeq.current
      setError(false)
      if (clear) setData(null)
      try {
        const resp = await api.get<AdvisorSummary>(`/advisors/${advisor.id}/summary?year=${year}`)
        if (mine !== requestSeq.current) return
        setData(resp)
        setYears(resp.years)
      } catch {
        if (mine !== requestSeq.current) return
        setError(true)
        setData(null)
      }
    },
    [advisor.id, year]
  )

  useEffect(() => {
    void load(true)
  }, [load])

  // Lo que se enseña y a lo que se dispara: el correo de la FICHA tal como la tiene el servidor ahora, no el de la lista.
  const email = data?.advisor.email ?? null
  const dataYear = data?.year ?? year

  // Orden por columna de «lo generado» (modal: solo en memoria). La oportunidad se ordena por lo que se lee, código y título.
  const { rows: lineRows, header } = useTableSort(
    data?.lines,
    {
      opportunity: (l) => `${l.code} ${l.title}`,
      stage: (l) => t(`enums:PipelineStage.${l.stage}`, { defaultValue: l.stage }),
      pct: (l) => l.commissionPct,
      base: (l) => l.baseCents,
      commission: (l) => l.commissionCents,
    },
    { persist: 'state' }
  )

  const date = (iso: string) => new Date(iso).toLocaleDateString(tag)

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key)
    try {
      await fn()
      await load(false)
    } catch (err) {
      toast.error(errorMessage(err))
      await load(false)
    } finally {
      setBusy(null)
    }
  }

  const liquidate = async () => {
    if (!data) return
    const notes = await promptDialog({
      title: t('settings:advisors.detail.liquidate.title', { year: dataYear }),
      message: t('settings:advisors.detail.liquidate.message', {
        amount: formatCents(data.totals.pendingCents),
        name: advisor.name,
      }),
      placeholder: t('settings:advisors.detail.liquidate.placeholder'),
      confirmLabel: t('settings:advisors.detail.liquidate.confirm'),
    })
    if (notes === null) return
    await run('liquidate', async () => {
      const resp = await api.post<{ settlement: AdvisorSettlementDto }>(
        `/advisors/${advisor.id}/settlements`,
        {
          year: dataYear,
          notes: notes.trim() || null,
          // Lo que se le enseñó: si ya no es lo pendiente, el servidor rechaza (409) en vez de liquidar otra cifra.
          expectedPendingCents: data.totals.pendingCents,
        }
      )
      toast.success(t('settings:advisors.detail.liquidate.done', { seq: resp.settlement.seq }))
    })
  }

  const download = (settlement: AdvisorSettlementDto | null) =>
    run(`pdf:${settlement?.id ?? 'year'}`, () =>
      downloadFile(
        `/advisors/${advisor.id}/report?year=${dataYear}${settlement ? `&settlementId=${settlement.id}` : ''}`,
        `comisiones-${dataYear}.pdf`
      )
    )

  const send = async (settlement: AdvisorSettlementDto | null) => {
    if (!email) return
    const ok = await confirmDialog({
      title: t('settings:advisors.detail.send.title'),
      message: settlement
        ? t('settings:advisors.detail.send.messageSettlement', {
            seq: settlement.seq,
            year: dataYear,
            email,
          })
        : t('settings:advisors.detail.send.messageYear', { year: dataYear, email }),
      confirmLabel: t('settings:advisors.detail.send.confirm'),
    })
    if (!ok) return
    await run(`send:${settlement?.id ?? 'year'}`, async () => {
      const resp = await api.post<{ sentTo: string }>(`/advisors/${advisor.id}/report/send`, {
        year: dataYear,
        ...(settlement ? { settlementId: settlement.id } : {}),
      })
      toast.success(t('settings:advisors.detail.send.done', { email: resp.sentTo }))
    })
  }

  const voidSettlement = async (s: AdvisorSettlementDto) => {
    const ok = await confirmDialog({
      message: t('settings:advisors.detail.void.message', {
        seq: s.seq,
        amount: formatCents(s.amountCents),
      }),
      confirmLabel: t('settings:advisors.detail.void.confirm'),
      tone: 'danger',
    })
    if (!ok) return
    await run(`void:${s.id}`, async () => {
      await api.post(`/advisors/${advisor.id}/settlements/${s.id}/void`, {})
      toast.success(t('settings:advisors.detail.void.done', { seq: s.seq }))
    })
  }

  const kpi = (label: string, value: string, tone?: 'warn' | 'good') => (
    <div className="rounded-lg border border-base px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider t-muted">{label}</div>
      <div
        className={`font-mono text-base mt-0.5 ${
          tone === 'warn'
            ? 'text-amber-700 dark:text-amber-300'
            : tone === 'good'
              ? 'text-emerald-700 dark:text-emerald-300'
              : 't-primary'
        }`}
      >
        {value}
      </div>
    </div>
  )

  const iconBtn =
    'w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200 disabled:opacity-30'

  return (
    <Modal
      open
      onClose={onClose}
      title={t('settings:advisors.detail.title', { name: advisor.name })}
      maxWidth="max-w-5xl"
    >
      <div className="space-y-4" data-testid="advisor-detail">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <label className="text-xs t-muted flex items-center gap-1.5">
            {t('settings:advisors.detail.year')}
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-jr2-500/50"
              data-testid="advisor-year"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          {data && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => void download(null)}
                disabled={busy !== null}
                className="px-3 py-1.5 rounded-lg text-sm border border-base t-secondary hover:bg-hover flex items-center gap-1.5 disabled:opacity-40"
              >
                <Download size={14} /> {t('settings:advisors.detail.actions.download')}
              </button>
              <button
                type="button"
                onClick={() => void send(null)}
                disabled={busy !== null || !email}
                title={email ? undefined : t('settings:advisors.detail.noEmail')}
                className="px-3 py-1.5 rounded-lg text-sm border border-base t-secondary hover:bg-hover flex items-center gap-1.5 disabled:opacity-40"
              >
                <Mail size={14} /> {t('settings:advisors.detail.actions.send')}
              </button>
              <button
                type="button"
                onClick={() => void liquidate()}
                disabled={busy !== null || data.totals.pendingCents <= 0}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5 disabled:opacity-40 disabled:hover:bg-jr2-600"
                data-testid="advisor-liquidate"
              >
                {busy === 'liquidate' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <ReceiptText size={14} />
                )}
                {t('settings:advisors.detail.actions.liquidate', { year: dataYear })}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
            <AlertCircle size={14} /> {t('settings:advisors.detail.error')}
          </div>
        )}
        {!data && !error && (
          <div className="p-8 text-center t-muted text-sm">
            <Loader2 size={16} className="inline-block animate-spin mr-2" />
            {t('settings:advisors.detail.loading')}
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
              {kpi(
                t('settings:advisors.detail.kpi.sold', { year: dataYear }),
                formatCents(data.totals.soldCents)
              )}
              {kpi(
                data.basis === 'FACTURADO'
                  ? t('settings:advisors.detail.kpi.invoiced', { year: dataYear })
                  : t('settings:advisors.detail.kpi.dealValue'),
                formatCents(data.totals.baseCents)
              )}
              {kpi(
                t('settings:advisors.detail.kpi.accrued'),
                formatCents(data.totals.accruedCents)
              )}
              {kpi(
                t('settings:advisors.detail.kpi.settled'),
                formatCents(data.totals.settledCents)
              )}
              {kpi(
                data.totals.pendingCents < 0
                  ? t('settings:advisors.detail.kpi.toOffset')
                  : t('settings:advisors.detail.kpi.pending'),
                formatCents(data.totals.pendingCents),
                data.totals.pendingCents < 0
                  ? 'warn'
                  : data.totals.pendingCents > 0
                    ? 'good'
                    : undefined
              )}
            </div>

            <p className="text-[11px] t-faint">
              {data.basis === 'VALOR_TRATO'
                ? t('settings:advisors.detail.hints.equity')
                : t('settings:advisors.detail.hints.settleOnly')}
            </p>

            <div>
              <h3 className="text-sm font-semibold t-primary mb-2">
                {t('settings:advisors.detail.lines.title', { year: dataYear })}
              </h3>
              <div className="surface overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
                    <tr>
                      {header('opportunity', t('settings:advisors.detail.lines.opportunity'))}
                      {header('stage', t('settings:advisors.detail.lines.stage'))}
                      {header('pct', t('settings:advisors.detail.lines.pct'), { align: 'right' })}
                      {header(
                        'base',
                        data.basis === 'FACTURADO'
                          ? t('settings:advisors.detail.lines.baseInvoiced')
                          : t('settings:advisors.detail.lines.baseDeal'),
                        { align: 'right' }
                      )}
                      {header('commission', t('settings:advisors.detail.lines.commission'), {
                        align: 'right',
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center t-muted">
                          {t('settings:advisors.detail.lines.empty', { year: dataYear })}
                        </td>
                      </tr>
                    )}
                    {lineRows.map((l) => (
                      <tr key={l.opportunityId} className="border-t border-base/40">
                        <td className="px-3 py-2">
                          <div className="t-primary">
                            <span className="font-mono text-xs t-faint mr-1.5">{l.code}</span>
                            {l.title}
                          </div>
                          <div className="text-xs t-muted">{l.clientName}</div>
                        </td>
                        <td className="px-3 py-2 text-xs t-secondary">
                          {t(`enums:PipelineStage.${l.stage}`, { defaultValue: l.stage })}
                        </td>
                        <td className="px-3 py-2 text-right font-mono t-secondary">
                          {l.commissionPct === null ? (
                            <span
                              className="text-amber-700 dark:text-amber-300"
                              title={t('settings:advisors.detail.lines.noPctTitle')}
                            >
                              {t('settings:advisors.detail.lines.noPct')}
                            </span>
                          ) : (
                            `${(l.commissionPct * 100).toLocaleString(tag, { maximumFractionDigits: 2 })} %`
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono t-secondary">
                          {formatCents(l.baseCents)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono t-primary">
                          {formatCents(l.commissionCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {data.lines.length > 0 && (
                    <tfoot>
                      <tr className="border-t border-base bg-[var(--bg-input-soft)] font-semibold">
                        <td className="px-3 py-2 t-primary" colSpan={3}>
                          {t('settings:advisors.detail.lines.total')}
                        </td>
                        <td className="px-3 py-2 text-right font-mono t-secondary">
                          {formatCents(data.totals.baseCents)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono t-primary">
                          {formatCents(data.totals.accruedCents)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              {data.pipeline.count > 0 && (
                <p className="text-xs t-muted mt-2">
                  {t('settings:advisors.detail.pipeline', {
                    count: data.pipeline.count,
                    value: formatCents(data.pipeline.valueCents),
                  })}
                </p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold t-primary mb-2">
                {t('settings:advisors.detail.settlements.title', { year: dataYear })}
              </h3>
              {data.settlements.length === 0 ? (
                <p className="text-sm t-muted">
                  {t('settings:advisors.detail.settlements.empty', { year: dataYear })}
                </p>
              ) : (
                <div className="surface overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {data.settlements.map((s) => (
                        <tr
                          key={s.id}
                          className={`border-t first:border-t-0 border-base/40 ${
                            s.voidedAt ? 'opacity-60' : ''
                          }`}
                        >
                          <td className="px-3 py-2 font-mono text-xs t-secondary whitespace-nowrap">
                            Nº {s.seq}
                          </td>
                          <td className="px-3 py-2 text-xs t-secondary whitespace-nowrap">
                            {date(s.settledAt)}
                            {s.settledByName && (
                              <span className="t-faint"> · {s.settledByName}</span>
                            )}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-mono t-primary ${
                              s.voidedAt ? 'line-through' : ''
                            }`}
                          >
                            {formatCents(s.amountCents)}
                          </td>
                          <td className="px-3 py-2 text-xs t-muted">
                            {s.voidedAt ? (
                              <span className="text-rose-700 dark:text-rose-300">
                                {t('settings:advisors.detail.settlements.voided', {
                                  date: date(s.voidedAt),
                                })}
                              </span>
                            ) : s.sentAt ? (
                              t('settings:advisors.detail.settlements.sent', {
                                email: s.sentTo,
                                date: date(s.sentAt),
                              })
                            ) : (
                              t('settings:advisors.detail.settlements.notSent')
                            )}
                            {s.notes && <div className="t-faint">{s.notes}</div>}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {!s.voidedAt && (
                              <span className="inline-flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => void download(s)}
                                  disabled={busy !== null}
                                  title={t('settings:advisors.detail.actions.download')}
                                  className={iconBtn}
                                >
                                  <Download size={13} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void send(s)}
                                  disabled={busy !== null || !email}
                                  title={
                                    email
                                      ? t('settings:advisors.detail.actions.send')
                                      : t('settings:advisors.detail.noEmail')
                                  }
                                  className={iconBtn}
                                >
                                  <Mail size={13} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void voidSettlement(s)}
                                  disabled={busy !== null}
                                  title={t('settings:advisors.detail.actions.void')}
                                  className={`${iconBtn} hover:!text-rose-500`}
                                >
                                  <Ban size={13} />
                                </button>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!email && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                  {t('settings:advisors.detail.noEmail')}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
